import { z } from 'zod';

/** CSV import (spec 04 › Import): parse, map columns, build a preview, upsert on external_ref. */

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  delimiter: string;
}

export function detectDelimiter(firstLine: string): string {
  const candidates = [';', ',', '\t'];
  let best = ',';
  let bestCount = -1;
  for (const d of candidates) {
    // count delimiters outside quotes
    let count = 0;
    let inQ = false;
    for (const ch of firstLine) {
      if (ch === '"') inQ = !inQ;
      else if (ch === d && !inQ) count++;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/** RFC 4180-ish parser: quoted fields, escaped quotes, CRLF, BOM. Sportlink uses ';'. */
export function parseCsv(input: string, delimiter?: string): ParsedCsv {
  const text = input.replace(/^﻿/, '');
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const d = delimiter ?? detectDelimiter(firstLine);
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') inQuotes = true;
    else if (ch === d) {
      record.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  const nonEmpty = records.filter((r) => r.some((c) => c.trim() !== ''));
  const [headerRow, ...rows] = nonEmpty;
  return { headers: (headerRow ?? []).map((h) => h.trim()), rows, delimiter: d };
}

export type TargetField =
  | 'external_ref'
  | 'full_name'
  | 'first_name'
  | 'infix'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'birth_date';

export const TARGET_FIELDS: { key: TargetField; label: string; hint?: string }[] = [
  { key: 'external_ref', label: 'Relatienummer', hint: 'Sleutel om bestaande personen bij te werken' },
  { key: 'full_name', label: 'Volledige naam' },
  { key: 'first_name', label: 'Voornaam / roepnaam' },
  { key: 'infix', label: 'Tussenvoegsel' },
  { key: 'last_name', label: 'Achternaam' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone', label: 'Telefoon' },
  { key: 'birth_date', label: 'Geboortedatum', hint: 'Alleen gebruikt om minderjarigen te herkennen' },
];

export type ColumnMapping = Partial<Record<TargetField, number>>;

const SYNONYMS: Record<TargetField, string[]> = {
  external_ref: ['relatienummer', 'relatiecode', 'lidnummer', 'bondsnummer', 'external_ref', 'id'],
  full_name: ['volledige naam', 'naam', 'full_name', 'volledigenaam'],
  first_name: ['roepnaam', 'voornaam', 'voornamen', 'first_name'],
  infix: ['tussenvoegsel', 'tussenvoegsels', 'tussenvoegsel(s)', 'infix'],
  last_name: ['achternaam', 'last_name'],
  email: ['e-mail', 'email', 'e-mailadres', 'emailadres', 'e-mail adres'],
  phone: ['mobiel', 'telefoon', 'telefoonnummer', 'mobiel nummer', 'mobielnummer', 'phone', 'telefoon mobiel'],
  birth_date: ['geboortedatum', 'geb. datum', 'birth_date', 'geboren'],
};

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

export function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const used = new Set<number>();
  for (const field of Object.keys(SYNONYMS) as TargetField[]) {
    const idx = headers.findIndex((h, i) => !used.has(i) && SYNONYMS[field].includes(norm(h)));
    if (idx >= 0) {
      mapping[field] = idx;
      used.add(idx);
    }
  }
  // A full-name column makes the split columns redundant only if those are absent.
  return mapping;
}

export interface MappedPerson {
  rowNumber: number; // 1-based, excluding header (spreadsheet row = rowNumber + 1)
  external_ref: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  is_minor: boolean;
}

const personSchema = z.object({
  full_name: z.string().min(2, 'Naam ontbreekt'),
  email: z.string().email('E-mailadres klopt niet').nullable(),
  phone: z
    .string()
    .regex(/^[+0-9 ()-]{6,20}$/, 'Telefoonnummer klopt niet')
    .nullable(),
  external_ref: z.string().max(64).nullable(),
});

/** Parses dd-mm-jjjj, dd/mm/jjjj or jjjj-mm-dd. */
export function parseDutchDate(s: string): Date | null {
  const t = s.trim();
  let m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(t);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return null;
}

export function isMinorOn(birth: Date, on: Date): boolean {
  const eighteen = new Date(birth.getFullYear() + 18, birth.getMonth(), birth.getDate());
  return eighteen.getTime() > on.getTime();
}

export type PreviewStatus = 'new' | 'update' | 'duplicate_in_file' | 'error';

export interface PreviewRow {
  status: PreviewStatus;
  person: MappedPerson;
  errors: string[];
  existingPersonId?: string;
}

export interface ImportPreview {
  rows: PreviewRow[];
  counts: Record<PreviewStatus, number>;
}

const cell = (row: string[], idx: number | undefined): string =>
  idx === undefined ? '' : (row[idx] ?? '').trim();

export function mapRow(row: string[], mapping: ColumnMapping, rowNumber: number, now: Date): MappedPerson {
  let fullName = cell(row, mapping.full_name);
  if (!fullName) {
    fullName = [cell(row, mapping.first_name), cell(row, mapping.infix), cell(row, mapping.last_name)]
      .filter(Boolean)
      .join(' ');
  }
  const birth = cell(row, mapping.birth_date);
  const birthDate = birth ? parseDutchDate(birth) : null;
  return {
    rowNumber,
    external_ref: cell(row, mapping.external_ref) || null,
    full_name: fullName.replace(/\s+/g, ' '),
    email: cell(row, mapping.email).toLowerCase() || null,
    phone: cell(row, mapping.phone) || null,
    is_minor: birthDate ? isMinorOn(birthDate, now) : false,
  };
}

export function mappingErrors(mapping: ColumnMapping): string[] {
  const errs: string[] = [];
  const hasName =
    mapping.full_name !== undefined || (mapping.first_name !== undefined && mapping.last_name !== undefined);
  if (!hasName) errs.push('Koppel "Volledige naam", of zowel "Voornaam" als "Achternaam".');
  if (mapping.external_ref === undefined)
    errs.push('Koppel "Relatienummer": zonder sleutel kunnen we bestaande personen niet herkennen.');
  return errs;
}

export function buildPreview(
  parsed: ParsedCsv,
  mapping: ColumnMapping,
  existing: { id: string; external_ref: string | null }[],
  now = new Date(),
): ImportPreview {
  const byRef = new Map(existing.filter((p) => p.external_ref).map((p) => [p.external_ref!, p.id]));
  const seen = new Set<string>();
  const rows: PreviewRow[] = parsed.rows.map((raw, i) => {
    const person = mapRow(raw, mapping, i + 1, now);
    const res = personSchema.safeParse(person);
    const errors = res.success ? [] : res.error.issues.map((iss) => iss.message);
    if (!person.external_ref) errors.push('Relatienummer ontbreekt');
    if (errors.length) return { status: 'error' as const, person, errors };
    const ref = person.external_ref!;
    if (seen.has(ref)) return { status: 'duplicate_in_file' as const, person, errors: ['Komt eerder in het bestand voor'] };
    seen.add(ref);
    const existingId = byRef.get(ref);
    return existingId
      ? { status: 'update' as const, person, errors: [], existingPersonId: existingId }
      : { status: 'new' as const, person, errors: [] };
  });
  const counts: Record<PreviewStatus, number> = { new: 0, update: 0, duplicate_in_file: 0, error: 0 };
  for (const r of rows) counts[r.status]++;
  return { rows, counts };
}
